import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import chatService, {
  type ChatMessage,
  type ChatSocketConnection,
  type ChatThread,
  connectToAppointment,
} from '../services/chatService';
import { useI18n } from '../contexts/I18nContext';

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface WebsocketPayload {
  type?: string;
  payload?: ChatMessage;
}

const emptyArray: ChatMessage[] = [];

const ChatModal: React.FC<ChatModalProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [filter, setFilter] = useState('');
  const [selectedThread, setSelectedThread] = useState<ChatThread | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>(emptyArray);
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendViaWhatsApp, setSendViaWhatsApp] = useState(false);
  const socketRef = useRef<ChatSocketConnection | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const closeSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    closeSocket();
    setMessages(emptyArray);
    setSelectedThread(null);
    setFilter('');
    setThreads([]);
    setIsLoadingThreads(false);
    setIsLoadingMessages(false);
    setIsSending(false);
    setNewMessage('');
  }, [closeSocket]);

  useEffect(() => {
    if (isOpen) {
      loadThreads();
    } else {
      resetState();
    }

    return () => {
      closeSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadThreads = async () => {
    setIsLoadingThreads(true);
    try {
      const response = await chatService.listThreads({ limit: 50, offset: 0 });
      const threadsArray = Array.isArray(response?.threads) ? response.threads : [];
      setThreads(threadsArray);
      if (threadsArray.length > 0) {
        await selectThread(threadsArray[0]);
      }
    } catch (error) {
      console.error('[ChatModal] Failed to load threads', error);
      setThreads([]);
    } finally {
      setIsLoadingThreads(false);
    }
  };

  const selectThread = useCallback(
    async (thread: ChatThread) => {
      setSelectedThread(thread);
      setIsLoadingMessages(true);
      closeSocket();

      try {
        const history = await chatService.getMessages(thread.appointment_id);
        const historyArray = Array.isArray(history) ? history : [];
        const ordered = [...historyArray].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        setMessages(ordered);
        await chatService.markAllRead(thread.appointment_id);

        setThreads(current => {
          if (!Array.isArray(current)) {
            return [];
          }

          return current.map(item =>
            item.appointment_id === thread.appointment_id
              ? {
                ...item,
                unread_count: 0,
                last_message: ordered.length
                  ? ordered[ordered.length - 1].message
                  : item.last_message,
                last_message_at: ordered.length
                  ? ordered[ordered.length - 1].created_at
                  : item.last_message_at,
              }
              : item
          );
        });

        socketRef.current = connectToAppointment(
          thread.appointment_id,
          (payload: WebsocketPayload) => {
            if (payload?.type === 'chat_message' && payload.payload) {
              upsertMessage(payload.payload);
              updateThreadPreview(payload.payload);
            }
          },
          event => console.warn('[ChatModal] WebSocket error', event),
          () => {
            console.info('[ChatModal] WebSocket closed');
          }
        );
      } catch (error) {
        console.error('[ChatModal] Failed to load messages', error);
      } finally {
        setIsLoadingMessages(false);
        scrollToBottom();
      }
    },
    [closeSocket]
  );

  const upsertMessage = useCallback((message: ChatMessage) => {
    setMessages(current => {
      const exists = current.some(item => item.id === message.id);
      if (exists) {
        return current;
      }
      const next = [...current, message];
      next.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      return next;
    });
  }, []);

  const updateThreadPreview = useCallback(
    (message: ChatMessage) => {
      setThreads(current => {
        if (!Array.isArray(current)) {
          return [];
        }

        const updated = current.map(thread => {
          if (thread.appointment_id === message.appointment_id) {
            const unreadIncrement =
              selectedThread && selectedThread.appointment_id === thread.appointment_id ? 0 : 1;
            return {
              ...thread,
              last_message: message.message,
              last_sender_type: message.sender_type,
              last_message_at: message.created_at,
              unread_count: (thread.unread_count || 0) + unreadIncrement,
            };
          }
          return thread;
        });

        updated.sort((a, b) => {
          const dateA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
          const dateB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
          return dateB - dateA;
        });

        return updated;
      });
    },
    [selectedThread]
  );

  const filteredThreads = useMemo(() => {
    if (!Array.isArray(threads)) {
      return [];
    }

    if (!filter.trim()) {
      return threads;
    }

    const term = filter.trim().toLowerCase();
    return threads.filter(thread => {
      return (
        thread.property_name?.toLowerCase().includes(term) ||
        thread.client_name?.toLowerCase().includes(term) ||
        thread.employee_name?.toLowerCase().includes(term)
      );
    });
  }, [threads, filter]);

  const handleSend = async () => {
    const content = newMessage.trim();
    if (!content || !selectedThread) {
      return;
    }

    setIsSending(true);

    try {
      if (socketRef.current) {
        socketRef.current.sendMessage({ type: 'message', message: content });
      } else {
        await chatService.sendMessage(selectedThread.appointment_id, {
          message: content,
          send_via_whatsapp: sendViaWhatsApp,
        });
      }

      setNewMessage('');
      setSendViaWhatsApp(false);
      scrollToBottom();
    } catch (error) {
      console.error('[ChatModal] Failed to send message', error);
    } finally {
      setIsSending(false);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 200);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div style={overlayStyles}>
      <div style={modalStyles}>
        <header style={headerStyles}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>{t('chat.title')}</h2>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '13px' }}>
              {t('chat.subtitle')}
            </p>
          </div>
          <button style={closeButtonStyles} onClick={onClose}>
            ✕
          </button>
        </header>

        <div style={contentStyles}>
          <aside style={sidebarStyles}>
            <div style={{ padding: '12px' }}>
              <label htmlFor="chat-filter-input" style={{ display: 'none' }}>
                {t('chat.filter')}
              </label>
              <input
                id="chat-filter-input"
                name="chat-filter"
                type="search"
                value={filter}
                onChange={event => setFilter(event.target.value)}
                placeholder={t('chat.filter')}
                style={filterInputStyles}
                autoComplete="off"
              />
            </div>
            <div style={threadListStyles}>
              {isLoadingThreads ? (
                <div style={placeholderStyles}>{t('chat.loadingThreads')}</div>
              ) : filteredThreads.length === 0 ? (
                <div style={placeholderStyles}>{t('chat.noThreads')}</div>
              ) : (
                filteredThreads.map(thread => (
                  <button
                    key={thread.thread_id}
                    onClick={() => selectThread(thread)}
                    style={{
                      ...threadButtonStyles,
                      backgroundColor:
                        selectedThread?.thread_id === thread.thread_id ? '#f3f4f6' : 'transparent',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <strong style={{ fontSize: '14px' }}>
                        {thread.property_name || t('chat.property')}
                      </strong>
                      {thread.unread_count > 0 && (
                        <span style={badgeStyles}>{thread.unread_count}</span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      {t('chat.client')} {thread.client_name || t('chat.client').replace(':', '')}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {t('chat.employee')} {thread.employee_name || t('chat.employee').replace(':', '')}
                    </div>
                    {thread.last_message && (
                      <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '6px' }}>
                        {thread.last_message}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </aside>

          <section style={messagesSectionStyles}>
            {selectedThread ? (
              <>
                <div style={threadHeaderStyles}>
                  <div>
                    <h3 style={{ margin: 0 }}>{selectedThread.property_name || t('chat.property')}</h3>
                    <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '12px' }}>
                      {t('chat.client')} {selectedThread.client_name || t('chat.client').replace(':', '')} · {t('chat.employee')}{' '}
                      {selectedThread.employee_name || t('chat.employee').replace(':', '')}
                    </p>
                  </div>
                </div>

                <div style={messageListStyles}>
                  {isLoadingMessages ? (
                    <div style={placeholderStyles}>{t('chat.loadingMessages')}</div>
                  ) : messages.length === 0 ? (
                    <div style={placeholderStyles}>{t('chat.noMessages')}</div>
                  ) : (
                    messages.map(message => (
                      <div
                        key={message.id}
                        style={{
                          ...messageBubbleStyles,
                          alignSelf: message.sender_type === 'employee' ? 'flex-end' : 'flex-start',
                          backgroundColor:
                            message.sender_type === 'employee' ? '#2563eb' : '#e5e7eb',
                          color: message.sender_type === 'employee' ? '#fff' : '#111827',
                        }}
                      >
                        <div style={{ fontSize: '12px', marginBottom: '4px', opacity: 0.9 }}>
                          {message.sender_name}
                          <span style={{ marginLeft: '8px', opacity: 0.8 }}>
                            {new Date(message.created_at).toLocaleString('pt-BR')}
                          </span>
                        </div>
                        <div style={{ fontSize: '14px', lineHeight: 1.4 }}>{message.message}</div>
                        {message.help_request && (
                          <div style={helpRequestStyles}>
                            {t('chat.helpRequest')}{' '}
                            {message.help_request.priority?.toUpperCase() || 'MÉDIA'}
                            {message.help_request.description && (
                              <p style={{ margin: '4px 0 0 0' }}>
                                {message.help_request.description}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div style={composerStyles}>
                  <label htmlFor="chat-message-input" style={{ display: 'none' }}>
                    {t('chat.messagePlaceholder')}
                  </label>
                  <textarea
                    id="chat-message-input"
                    name="chat-message"
                    placeholder={t('chat.messagePlaceholder')}
                    value={newMessage}
                    onChange={event => setNewMessage(event.target.value)}
                    rows={3}
                    style={textAreaStyles}
                    autoComplete="off"
                  />
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <label htmlFor="chat-whatsapp-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                      <input
                        id="chat-whatsapp-checkbox"
                        name="send-via-whatsapp"
                        type="checkbox"
                        checked={sendViaWhatsApp}
                        onChange={e => setSendViaWhatsApp(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ color: '#25D366' }}>📱</span>
                      <span style={{ color: '#6b7280' }}>{t('chat.sendWhatsApp')}</span>
                    </label>
                    <button
                      style={sendButtonStyles}
                      onClick={handleSend}
                      disabled={isSending || !newMessage.trim()}
                    >
                      {isSending ? t('chat.sending') : t('chat.send')}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div style={placeholderStyles}>{t('chat.selectThread')}</div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

const overlayStyles: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(17, 24, 39, 0.6)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  padding: '24px',
};

const modalStyles: React.CSSProperties = {
  width: '960px',
  maxWidth: '100%',
  height: '640px',
  backgroundColor: '#fff',
  borderRadius: '12px',
  boxShadow: '0 20px 45px rgba(15, 23, 42, 0.2)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyles: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px',
  borderBottom: '1px solid #e5e7eb',
};

const closeButtonStyles: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  fontSize: '20px',
  cursor: 'pointer',
  color: '#6b7280',
};

const contentStyles: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
};

const sidebarStyles: React.CSSProperties = {
  width: '320px',
  borderRight: '1px solid #e5e7eb',
  display: 'flex',
  flexDirection: 'column',
};

const filterInputStyles: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  outline: 'none',
  fontSize: '14px',
};

const threadListStyles: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '0 12px 12px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const threadButtonStyles: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  padding: '12px',
  textAlign: 'left',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const badgeStyles: React.CSSProperties = {
  backgroundColor: '#2563eb',
  color: '#fff',
  borderRadius: '12px',
  padding: '2px 8px',
  fontSize: '12px',
};

const messagesSectionStyles: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};

const threadHeaderStyles: React.CSSProperties = {
  padding: '16px 20px',
  borderBottom: '1px solid #e5e7eb',
};

const messageListStyles: React.CSSProperties = {
  flex: 1,
  padding: '20px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const messageBubbleStyles: React.CSSProperties = {
  padding: '12px 16px',
  borderRadius: '12px',
  maxWidth: '70%',
  boxShadow: '0 2px 12px rgba(15, 23, 42, 0.12)',
};

const helpRequestStyles: React.CSSProperties = {
  marginTop: '8px',
  padding: '8px 10px',
  borderRadius: '8px',
  backgroundColor: '#fee2e2',
  color: '#991b1b',
  fontSize: '12px',
};

const placeholderStyles: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  color: '#6b7280',
  fontSize: '14px',
  padding: '24px',
};

const composerStyles: React.CSSProperties = {
  padding: '16px 20px',
  borderTop: '1px solid #e5e7eb',
  display: 'flex',
  gap: '12px',
};

const textAreaStyles: React.CSSProperties = {
  flex: 1,
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  padding: '12px',
  resize: 'none',
  fontSize: '14px',
  lineHeight: 1.4,
};

const sendButtonStyles: React.CSSProperties = {
  backgroundColor: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '12px 16px',
  cursor: 'pointer',
  fontWeight: 600,
  minWidth: '120px',
};

export default ChatModal;
